import { baseURL, getIdToken } from "./client";

import axios from 'axios';

// Attach server error message to every error response so UI can show it
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const data = err.response?.data;
    let serverMessage = null;
    if (data != null) {
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          serverMessage = parsed?.message ?? parsed?.error ?? null;
        } catch (_) {
          serverMessage = data;
        }
      } else {
        serverMessage = data?.message ?? data?.error ?? null;
      }
      if (serverMessage != null) err.serverMessage = String(serverMessage);
    }
    return Promise.reject(err);
  },
);

// Axios

/**
 *
 * @param {{pageSize: number, pageNumber:number, column:string, order: string, statuses: string[],shipmentTypes:string[], shipmentTatCriteria: string[],  createdAtGtEq: ,createdAtLtEq}} parameters Parameters for the get Shipments API call
 * @returns
 */
export const getShipments = async ({
  pageSize,
  pageNumber,
  column,
  order,
  search,
  clientUid,
  statuses = [],
  shipmentTypes = [],
  pickupDateGtEq,
  pickupDateLtEq,
  deliveryDateGtEq,
  deliveryDateLtEq,
  createdAtGtEq,
  createdAtLtEq,
  actualDeliveryDateGtEq,
  actualDeliveryDateLtEq,
  actualPickupDateGtEq,
  actualPickupDateLtEq,
  expectedAttemptDateGtEq,
  expectedAttemptDateLtEq,
  scheduledAttemptDateGtEq,
  scheduledAttemptDateLtEq,
  shipmentTatCriteria = [],
  handoverModes = [],
  tags,
  serviceRequests,
}) => {
  return axios({
    baseURL,
    url: "/shipments",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    params: {
      pageSize,
      pageNumber,
      column,
      order,
      search,
      clientUid,
      statuses,
      shipmentTypes,
      pickupDateGtEq,
      pickupDateLtEq,
      deliveryDateGtEq,
      deliveryDateLtEq,
      createdAtGtEq,
      createdAtLtEq,
      actualDeliveryDateGtEq,
      actualDeliveryDateLtEq,
      actualPickupDateGtEq,
      actualPickupDateLtEq,
      expectedAttemptDateGtEq,
      expectedAttemptDateLtEq,
      scheduledAttemptDateGtEq,
      scheduledAttemptDateLtEq,
      tags,
      serviceRequests,
    },
  }).then(response => response.data);
};

/**
 * Downloads a filtered shipments export as an .xlsx file.
 * Accepts the same filter params as getShipments (minus pageSize/pageNumber);
 * the backend applies brand scoping and streams the workbook.
 *
 * @param {object} params Same filter params used for getShipments
 */
export const exportShipments = async (params = {}) => {
  let response;
  try {
    response = await axios({
      baseURL,
      url: "/shipments/export",
      method: "GET",
      responseType: "blob",
      headers: {
        Authorization: `bearer ${await getIdToken()}`,
      },
      params,
    });
  } catch (error) {
    // Error bodies arrive as a Blob (responseType: 'blob'); read the JSON message.
    const blob = error.response?.data;
    if (blob && typeof blob.text === "function") {
      try {
        const parsed = JSON.parse(await blob.text());
        const message = parsed?.message ?? parsed?.error;
        if (message) error.serverMessage = String(message);
      } catch (_) {
        // leave error as-is
      }
    }
    throw error;
  }

  // Resolve filename from Content-Disposition, else fall back to a dated name.
  const disposition = response.headers["content-disposition"] || "";
  const match = disposition.match(/filename="?([^"]+)"?/);
  const filename =
    (match && match[1]) ||
    `shipments-export-${new Date().toISOString().slice(0, 10)}.xlsx`;

  const blob = new Blob([response.data], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 *
 * @param {number} waybillNumber Shipment's waybill number
 * @returns object
 */
export const getShipmentByWaybillNumber = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}`,
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

/**
 *
 * @param {number} waybillNumber
 * @returns {number}
 */

export const cloneShipment = async waybillNumber => {
  try {
    const res = await axios({
      baseURL,
      url: `/shipments/${waybillNumber}/clone`,
      method: `POST`,
      headers: {
        Authorization: `bearer ${await getIdToken()}`,
      },
    });

    return res.data;
  } catch (e) {
    console.log(e);
  }
};

export const updateShipment = payload => ({ payload });

/**
 *
 * @param {string} waybillNumber
 * @returns
 */
export const cancelShipment = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/cancel`,
    method: "PATCH",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

export const createShipment = payload => ({ shipment: 1 });

/*
fetch(`${BASE_URL}/shipments/${waybillNumber}/boxes`)

*/

export const getBoxes = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/boxes`,
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

export const getItems = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/items`,
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

export const getServiceRequests = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/service-requests`,
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

export const createServiceRequest = async (waybillNumber, serviceType) => {
  return axios({
    baseURL,
    method: "POST",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    url: `/shipments/${waybillNumber}/service-requests`,
    data: { serviceType },
  });
};
/*
fetch(`${BASE_URL}/shipments/{waybillNumber}/tracking-updates`)

*/

export const getPublicShipmentData = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/public`,
  }).then(response => response.data);
};

export const trackShipments = async waybillNumber =>
  axios({
    baseURL,
    url: `/shipments/${waybillNumber}/tracking-updates`,
  }).then(response => response.data);

/**
 *
 * @param {number} waybillNumber
 * @param {string} status
 * @returns
 */
export const updateShipmentStatus = async (waybillNumber, status) =>
  axios({
    baseURL,
    url: `/shipments/${waybillNumber}/change-status`,
    method: "PATCH",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    data: { status },
  }).then(response => response.data);

export const createMultipleShipmentsFromFileUpload = async (
  file,
  clientUid,
) => {
  try {
    let data = new FormData();
    data.append("shipments", file, file.name);
    if (clientUid) {
      data.append("clientUid", clientUid);
    }
    await axios({
      baseURL,
      url: "/shipments/upload",
      method: "POST",
      headers: {
        Authorization: `bearer ${await getIdToken()}`,
        "Content-Type": "multipart/form-data",
      },
      data,
    });

    return true;
  } catch (error) {
    console.log(`Unable to upload file:`, error);
    return false;
  }
};

export const createMultipleShipmentsFromValidatedJSON = async (
  data,
  clientUid,
  templateUid,
) => {
  try {
    if (!clientUid) {
      throw new Error('client context required');
    }
    return axios({
      baseURL,
      url: '/shipments/batch-create',
      method: 'POST',
      headers: {
        Authorization: `bearer ${await getIdToken()}`,
        'Content-Type': 'multipart/form-data',
      },
      data: { items: data, clientUid, templateUid },
    });
  } catch (error) {
    const data = error.response?.data;
    let serverMessage = error.serverMessage;
    if (!serverMessage && data != null) {
      if (typeof data === 'string') {
        try {
          const parsed = JSON.parse(data);
          serverMessage = parsed?.message ?? parsed?.error ?? null;
        } catch (_) {
          serverMessage = data;
        }
      } else {
        serverMessage =
          data?.message ?? data?.error ??
          (Array.isArray(data?.errors) && data.errors.length ? data.errors.map(String).join(', ') : null);
      }
      if (serverMessage) serverMessage = String(serverMessage).trim();
    }
    const message = serverMessage || error.message || 'Request failed';
    const err = new Error(message);
    err.response = error.response;
    throw err;
  }
};

export const getShipmentIngestionTemplates = async (clientUid) => {
  try {
    if (!clientUid) {
      return;
    }

    return axios({
      baseURL,
      url: '/ingestion-templates/shipments',
      method: 'GET',
      headers: {
        Authorization: `bearer ${await getIdToken()}`,
        'Content-Type': 'multipart/form-data',
      },
      params: { clientUid },
    });
  } catch (error) {
    return { error: `${error.message}` };
  }
};

export const createReturnShipment = async ({
  waybillNumber,
  items,
  requestedPickupDate,
  reversePickupInfo,
}) => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/return`,
    method: "POST",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    data: {
      requestedPickupDate,
      reversePickupInfo,
      items,
    },
  }).then(response => response.data);
};

export const createReplacementShipment = async ({
  waybillNumber,
  items,
  requestedPickupDate,
  reversePickupInfo,
}) => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/replacement`,
    method: "POST",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    data: {
      requestedPickupDate,
      reversePickupInfo,
      items,
    },
  }).then(response => response.data);
};

export const reprintLabels = async ({ waybillNumber }) => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/label`,
    method: "POST",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
  }).then(response => response.data);
};

export const shipmentRequestRTO = async waybillNumber => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/action`,
    method: "POST",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    data: {
      type: "rto_requested",
      payload: {},
    },
  });
};

export const rescheduleShipment = async ({ waybillNumber, date }) => {
  return axios({
    baseURL,
    url: `/shipments/${waybillNumber}/reschedule`,
    method: "PATCH",
    headers: {
      Authorization: `bearer ${await getIdToken()}`,
    },
    data: {
      date,
    },
  }).then(response => response.data);
};
